import { useState } from "react";
import Button from "../../../student/components/generic/button";
import { formatNumber } from "../../utils";

export const IndividualQuoteRequestAmount: React.FC<{
  licenseType: "plus" | "integral" | "both";
  previousLicenseType: "plus" | "integral" | "both";
  individualPlus: number;
  individualIntegral: number;
  pricePerPlus: number;
  pricePerIntegral: number;
  isDownloading: boolean;
  onSelect: (licenseType: "plus" | "integral" | "both") => Promise<void>;
  disabled: boolean;
}> = ({
  licenseType,
  previousLicenseType,
  individualPlus,
  individualIntegral,
  pricePerPlus,
  pricePerIntegral,
  onSelect,
  isDownloading,
  disabled,
}) => {
  const plusSubtotal = pricePerPlus * individualPlus;
  const integralSubtotal = pricePerIntegral * individualIntegral;
  const total =
    licenseType === "both"
      ? plusSubtotal + integralSubtotal
      : licenseType === "plus"
      ? plusSubtotal
      : integralSubtotal;

  const [selectedLicenseType, setSelectedLicenseType] = useState(licenseType);

  // Two-column upsell to integral
  // If previously only had plus, give them the option to upgrade
  if (previousLicenseType === "plus") {
    return (
      <div className="flex flex-col gap-10 md:flex-row md:gap-4">
        <div className="w-full rounded-lg bg-dm-background-blue-100 p-4 pt-6">
          <h4 className="mb-1 font-bold">Your Cost</h4>
          <div className="mb-6 text-sm">
            <strong className="mr-[2px] text-lg">
              ${formatNumber(pricePerPlus * individualPlus)}
            </strong>{" "}
            Total for {formatNumber(individualPlus)} PLUS Licenses
          </div>
          <Button
            className="w-full"
            isLoading={isDownloading && selectedLicenseType === "plus"}
            onClick={() => {
              onSelect("plus");
              setSelectedLicenseType("plus");
            }}
            disabled={
              disabled || (isDownloading && selectedLicenseType === "integral")
            }
            submit={false}
          >
            Download PLUS Quote
          </Button>
        </div>
        <div className="relative w-full rounded-lg bg-dm-brand-blue-100 p-4 pt-6">
          <span className="absolute -top-[15px] left-0 flex w-full justify-center">
            <span className="flex h-[30px] items-center rounded-full border border-dm-brand-blue-500 bg-white px-4 text-sm font-bold text-dm-brand-blue-500">
              Upgraded License &ndash; Recommended
            </span>
          </span>
          <h4 className="mb-1 font-bold">Your Cost with Upgrade</h4>
          <div className="mb-6 text-sm">
            <strong className="mr-[2px] text-lg">
              ${formatNumber(pricePerIntegral * individualPlus)}
            </strong>{" "}
            Total for {formatNumber(individualPlus)} INTEGRAL Licenses
          </div>
          <Button
            className="w-full"
            isLoading={isDownloading && selectedLicenseType === "integral"}
            onClick={() => {
              onSelect("integral");
              setSelectedLicenseType("integral");
            }}
            disabled={
              disabled || (isDownloading && selectedLicenseType === "plus")
            }
            submit={false}
          >
            Download INTEGRAL Quote
          </Button>
        </div>
      </div>
    );
  }

  // One column with two line items
  // Only an option if previously had both. Will only display if "plus and
  // integral" is selected
  if (licenseType === "both") {
    return (
      <div className="flex flex-col gap-2 rounded-lg bg-dm-background-blue-100 p-4">
        <h4 className="font-bold">Your Estimated Cost</h4>
        <ul className="flex flex-col gap-2 border-b border-dm-gray-200 py-2 text-sm">
          <li>
            <strong className="mr-[1px]">
              ${formatNumber(individualPlus * pricePerPlus)}
            </strong>{" "}
            for {formatNumber(individualPlus)} PLUS licenses
          </li>
          <li>
            <strong className="mr-[1px]">
              ${formatNumber(individualIntegral * pricePerIntegral)}
            </strong>{" "}
            for {formatNumber(individualIntegral)} INTEGRAL licenses
          </li>
        </ul>
        <div className="text-sm">
          <strong className="mr-[1px] text-lg">${formatNumber(total)}</strong>{" "}
          Total
        </div>
      </div>
    );
  }

  // Default: one column with only the total
  // Used when only one license type is selected and previous license type is
  // not "plus"
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-dm-background-blue-100 p-4">
      <h4 className="font-bold">Your Cost</h4>
      <div className="text-sm">
        <strong className="mr-[2px] text-lg">${formatNumber(total)}</strong>{" "}
        {licenseType === "plus"
          ? `Total for ${formatNumber(individualPlus)} PLUS Licenses`
          : `Total for ${formatNumber(individualIntegral)} INTEGRAL Licenses`}
      </div>
    </div>
  );
};
